import * as FileSystem from 'expo-file-system/legacy';
import mime from 'mime';
import { Platform } from 'react-native';
import { launchImageLibrary } from 'react-native-image-picker';
import uuid from 'react-native-uuid';

import { contentType } from '@utils/fileUtils';
import { compressImages } from '@utils/imageCompression';

import { LocalFile } from '../graphql/generated';

let RNFS: any;
if (Platform.OS !== 'web') {
  RNFS = require('react-native-fs');
}

export const selectImageFromGallery = async (
  selectionLimit?: number
): Promise<LocalFile[] | undefined> => {
  const result = await (launchImageLibrary
    ? launchImageLibrary({
        mediaType: 'mixed',
        quality: 1,
        selectionLimit: selectionLimit ?? 1,
        assetRepresentationMode: 'current',
      })
    : Promise.reject(new Error('Image picker not available')));

  if (!result.didCancel) {
    const localFiles = await Promise.all(
      (result?.assets ?? []).map(async (item): Promise<LocalFile> => {
        const clientId = uuid.v4().toString();

        if (!item.fileSize && item.uri) {
          const fileInfo = await FileSystem.getInfoAsync(item.uri, {
            size: true,
          });
          item.fileSize = fileInfo.size ?? 0;
        }

        if (!item.type && item.uri?.startsWith('data:')) {
          item.type = contentType(item.uri);
        }

        let ext =
          item.fileName?.split('.').pop() ?? mime.getExtension(item.type ?? '');
        if (!ext) ext = 'jpg';

        const fileName = `${clientId}.${ext}`;
        const destPath = `${RNFS.DocumentDirectoryPath}/${fileName}`;
        const newItem = {
          ...item,
          uri: decodeURI(item?.uri ?? ''),
        };

        try {
          await RNFS.copyFile(newItem.uri, destPath);
        } catch (err) {
          console.error('Cache image failed:', err);
        }

        // NOTE: cdnBaseUrl and path values are empty string to satisfy
        // Image type constraints. These are ignored by local images and
        // included when an Image is returned from the server.
        return {
          __typename: 'LocalFile',
          id: clientId,
          name: fileName,
          size: item.fileSize,
          clientId,
          url: `file://${destPath}`,
          cdnBaseUrl: '',
          path: '',
          contentType: item.type ?? 'application/octet-stream',
          isImage: Platform.select({
            default: true,
            web: item.type?.startsWith('image'),
          }),
          isAudio: false,
          isVideo: !item.type?.startsWith('image'),
          isPreviewable: false,
          thumbnail: '',
        };
      })
    );

    const compressedImages = await compressImages(localFiles);
    return compressedImages;
  }
};
